import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthService, UserType } from 'src/app/domain/auth/service/auth.service';
import { NotificationsService } from '../../core/service/notifications.service';
import { GuardReturnType } from "./util";
import { NewUserComponent } from "../../domain/login/new-user-component/new-user.component";
import { FRONT_URLS } from "../common/url-parts";

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private cookie = inject(CookieService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private notificationService = inject(NotificationsService);

  async canActivate(activatedRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): GuardReturnType {
    const authCookie = this.cookie.get('freemium_user_info');
    const isTokenAvailable = this.checkTokens(authCookie);
    if (!isTokenAvailable) {
      await this.router.navigateByUrl(FRONT_URLS.LOGIN);
      return false;
    }
    await this.checkExpirationDate(authCookie);
    this.checkUserType();
    this.auth.changeIsLoggedIn(activatedRoute.routeConfig?.component !== NewUserComponent); // TODO: make it signal too
    // Otherwise, it will call all subscribers on each component loading;
    return true;
  }

  private getLoggedInUserType(): UserType {
    const isAdmin = window.location.pathname.includes('admin');
    return isAdmin ? 'admin' : 'viewer';
  }

  private async checkExpirationDate(authCookie: string): Promise<void> {
    const splitAuthCookie = authCookie.split('&');
    const expDate = Number(splitAuthCookie[3]);
    const currentDate = Math.trunc((new Date()).getTime() / 1000);
    if (expDate < currentDate) {
      this.notificationService.warning({
        title: 'Cookies expired',
        message: `Auth cookie is ${currentDate - expDate} seconds expired.`,
      });
      await this.router.navigateByUrl(FRONT_URLS.LOGIN);
    }
  }

  private checkTokens(authCookie: string): boolean {
    if (authCookie) {
      return true;
    }
    this.notificationService.warning({ title: 'Cookies not found', message: 'Auth token is not provided.' });
    return false;
  }

  /**
   * Checks the current user type and sets it if it's undefined.
   * If the current user type is undefined, this method tries to retrieve it from local storage.
   * If it is not found in local storage, the user type is set to the value returned by `getLoggedInUserType()`.
   *
   * NOTE: `localStorage.getItem()` returns the string `'undefined'` if the userType is not found in local storage.
   * This method includes an extra check to handle that behaviour.
   */
  // save admin or viewer as current user type after page reload.
  private checkUserType(): void {
    const userType = this.auth.getCurrentUserType();
    if (!userType) {
      const userTypeFromLocalStorageString = localStorage.getItem('userType');
      this.auth.setCurrentUserType(userTypeFromLocalStorageString !==
      'undefined' ? userTypeFromLocalStorageString as UserType : this.getLoggedInUserType());
    }
  }
}
